// Plan Types - Plan Remodel
// These types support the new dynamic plan system with add-ons

export interface Plan {
  id: string;
  name: string;
  displayName: string;
  description: string;
  monthlyPrice: number; // in cents
  currency: string;
  formattedPrice: string;

  // Included resources
  agentsIncluded: number;
  storageIncludedGB: number;

  // Limits
  maxAgents?: number | null; // null = unlimited

  // Extra agent add-on
  extraAgentPrice?: number | null; // null = not available
  extraAgentStorageGB?: number | null; // bonus storage per extra agent

  // Extra storage add-on
  extraStoragePrice: number;
  extraStorageAmountGB: number;

  // Feature flags
  features: PlanFeatures;

  // Stripe integration
  stripePriceId?: string;
  stripeProductId?: string;
  addonStripeIds?: {
    agentAddonProductId?: string;
    agentAddonPriceId?: string;
    storageAddonProductId?: string;
    storageAddonPriceId?: string;
  };

  // Plan availability
  isActive: boolean;
  isPublic: boolean;
  sortOrder: number;
  baseMargin?: number;
  createdAt: string;
  updatedAt: string;

  // Computed fields
  hasExtraAgents: boolean;
  hasExtraStorage: boolean;
  hasUnlimitedAgents: boolean;

  // Stripe sync status
  stripeSynced?: boolean;
  stripeSyncError?: string;
}

export interface PlanFeatures {
  retentionDays: number;
  support: 'email' | 'priority' | 'phone';
  apiAccess: boolean;
  customPolicies?: boolean;
  sso?: boolean;
  auditLogs?: boolean;
  compliance?: boolean;
  minScheduleInterval?: number;
}

export interface PlanLimits {
  minScheduleInterval: number;
  maxAgents: number;
  maxStorageGB: number;
  retentionDays: number;
}

export interface SubscriptionWithAddons {
  id: string;
  organizationId: string;
  status: string;
  planName: string;
  planAmount: number;
  planCurrency: string;
  planInterval: string;
  planStorageGB: number;
  planAgentLimit: number;
  planBackupLimit: number;
  currentPeriodStart?: string;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  trialStart?: string;
  trialEnd?: string;
  createdAt: string;
  stripeSubscriptionId?: string;
  stripeCustomerId?: string;
  stripePriceId?: string;

  // Add-on fields (Plan Remodel)
  extraAgents: number;
  extraStorageGB: number;
  extraAgentCost: number;
  extraStorageCost: number;
  effectiveAgentLimit: number;
  effectiveStorageGB: number;
  totalMonthlyCost: number;
}

export interface AddonsAvailable {
  extraAgent: boolean;
  extraStorage: boolean;
  extraAgentPrice?: number;
  extraAgentStorageGB?: number;
  extraStoragePrice: number;
  extraStorageAmountGB: number;
}

export interface SubscriptionResponse {
  subscription: SubscriptionWithAddons;
  addonsAvailable?: AddonsAvailable;
}

export interface CreatePlanRequest {
  name: string;
  displayName: string;
  description?: string;
  monthlyPrice: number;
  currency?: string;
  agentsIncluded: number;
  storageIncludedGB: number;
  maxAgents?: number | null;
  extraAgentPrice?: number | null;
  extraAgentStorageGB?: number | null;
  extraStoragePrice: number;
  extraStorageAmountGB?: number;
  features?: PlanFeatures;
  stripePriceId?: string;
  stripeProductId?: string;
  isActive?: boolean;
  isPublic?: boolean;
  sortOrder?: number;
  baseMargin?: number;
  syncWithStripe?: boolean; // Auto-sync with Stripe
}

export interface UpdatePlanRequest extends Partial<CreatePlanRequest> {
  id: string;
}

export interface AddAddonRequest {
  addonType: 'EXTRA_AGENT' | 'EXTRA_STORAGE';
  quantity: number;
}

export interface AddAddonResponse {
  message: string;
  addon_type: string;
  quantity: number;
  unit_cost: number;
  total_cost: number;
  new_limits: {
    agent_limit: number;
    storage_gb: number;
  };
}

export interface SubscriptionAddon {
  id: string;
  subscriptionId: string;
  organizationId: string;
  addonType: 'EXTRA_AGENT' | 'EXTRA_STORAGE';
  quantity: number;
  unitCost: number;
  totalCost: number;
  storageGbPerUnit?: number;
  storageGbPerAgent?: number;
  createdAt: string;
  createdBy?: string;
}

// Helper functions
export function formatPrice(
  cents: number,
  currency: string = 'BRL',
  locale: string = 'pt-BR'
): string {
  const value = cents / 100;
  try {
    const parts = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).formatToParts(value);
    return parts
      .map((part, index) =>
        currency === 'BRL' && part.type === 'currency' && parts[index + 1]?.type === 'integer'
          ? `${part.value}\u00a0`
          : part.value
      )
      .join('');
  } catch {
    // Keep the previous BRL formatting as a safe fallback for invalid locales/currencies.
    if (currency === 'BRL') {
      return `R$ ${value.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `$ ${value.toFixed(2)}`;
  }
}

export function formatPriceShort(cents: number, currency: string = 'BRL'): string {
  const value = cents / 100;
  if (currency === 'BRL') {
    return `R$ ${Math.round(value)}`;
  }
  return `$ ${Math.round(value)}`;
}

export function calculatePlanMonthlyCost(
  plan: Plan,
  extraAgents: number,
  extraStorageUnits: number
): number {
  let total = plan.monthlyPrice;
  if (plan.extraAgentPrice) {
    total += extraAgents * plan.extraAgentPrice;
  }
  total += extraStorageUnits * plan.extraStoragePrice;
  return total;
}

export function getPlanFeaturesList(plan: Plan): string[] {
  const features: string[] = [];
  const f = plan.features;

  // Base features
  features.push(`${plan.agentsIncluded} agent${plan.agentsIncluded > 1 ? 's' : ''}`);
  features.push(`${plan.storageIncludedGB} GB storage`);
  features.push(`${f?.retentionDays || 7}-day retention`);

  // Add-on features
  if (plan.extraAgentPrice) {
    features.push(`Extra agents: ${formatPrice(plan.extraAgentPrice, plan.currency)}`);
  }
  if (plan.extraStoragePrice > 0) {
    features.push(
      `Extra storage: ${formatPrice(plan.extraStoragePrice, plan.currency)}/${plan.extraStorageAmountGB}GB`
    );
  }

  // Feature flags
  if (f?.apiAccess) features.push('API access');
  if (f?.support === 'priority') features.push('Priority support');
  if (f?.support === 'phone') features.push('Phone support');
  if (f?.customPolicies) features.push('Custom policies');
  if (f?.sso) features.push('SSO');
  if (f?.auditLogs) features.push('Audit logs');
  if (f?.compliance) features.push('Compliance features');

  return features;
}
